/**
 * Nganya vehicle catalog.
 * To add a 7th vehicle: drop PNGs in public/assets/vehicles/, append a definition here,
 * map an unlock achievement, then rebuild.
 */

export type MatatuRarity = 'common' | 'rare' | 'epic' | 'legendary';

export type MatatuDefinition = {
  id: string;
  name: string;
  description: string;
  spriteKey: string;
  thumbnailKey: string;
  /** Path relative to public/ — gameplay size */
  spritePath: string;
  /** Path relative to public/ — garage preview */
  thumbnailPath: string;
  rarity: MatatuRarity;
  unlockedByDefault: boolean;
  /** Achievement id that unlocks this vehicle, or 'starter' */
  unlock: string;
  accent: string;
};

export const VEHICLES: MatatuDefinition[] = [
  {
    id: 'nairobi-legend',
    name: 'Nairobi Legend',
    description: 'Flagship black-and-gold matatu. The signature PlayHub ride.',
    spriteKey: 'matatu-nairobi-legend',
    thumbnailKey: 'thumb-nairobi-legend',
    spritePath: 'assets/vehicles/nairobi-legend-sm.png',
    thumbnailPath: 'assets/vehicles/nairobi-legend.png',
    rarity: 'legendary',
    unlockedByDefault: true,
    unlock: 'starter',
    accent: '#ffb703',
  },
  {
    id: 'neon-beast',
    name: 'Neon Beast',
    description: 'Electric purple and cyan. Built for night runs on Ngong Road.',
    spriteKey: 'matatu-neon-beast',
    thumbnailKey: 'thumb-neon-beast',
    spritePath: 'assets/vehicles/neon-beast-sm.png',
    thumbnailPath: 'assets/vehicles/neon-beast.png',
    rarity: 'epic',
    unlockedByDefault: false,
    unlock: 'first_run',
    accent: '#00f5d4',
  },
  {
    id: 'mtaa-express',
    name: 'Mtaa Express',
    description: 'Bright yellow street energy. Impossible to miss in traffic.',
    spriteKey: 'matatu-mtaa-express',
    thumbnailKey: 'thumb-mtaa-express',
    spritePath: 'assets/vehicles/mtaa-express-sm.png',
    thumbnailPath: 'assets/vehicles/mtaa-express.png',
    rarity: 'common',
    unlockedByDefault: false,
    unlock: 'fare_collector',
    accent: '#ffd60a',
  },
  {
    id: 'coastal-cruiser',
    name: 'Coastal Cruiser',
    description: 'Turquoise, coral and white — Mombasa vibes on every lane change.',
    spriteKey: 'matatu-coastal-cruiser',
    thumbnailKey: 'thumb-coastal-cruiser',
    spritePath: 'assets/vehicles/coastal-cruiser-sm.png',
    thumbnailPath: 'assets/vehicles/coastal-cruiser.png',
    rarity: 'rare',
    unlockedByDefault: false,
    unlock: 'road_survivor',
    accent: '#00b4d8',
  },
  {
    id: 'shujaa',
    name: 'Shujaa',
    description: 'Forest green and gold. Confidence on four wheels.',
    spriteKey: 'matatu-shujaa',
    thumbnailKey: 'thumb-shujaa',
    spritePath: 'assets/vehicles/shujaa-sm.png',
    thumbnailPath: 'assets/vehicles/shujaa.png',
    rarity: 'epic',
    unlockedByDefault: false,
    unlock: 'combo_king',
    accent: '#d4a373',
  },
  {
    id: 'midnight-king',
    name: 'Midnight King',
    description: 'Navy, silver and magenta drama. Unlock and own the night.',
    spriteKey: 'matatu-midnight-king',
    thumbnailKey: 'thumb-midnight-king',
    spritePath: 'assets/vehicles/midnight-king-sm.png',
    thumbnailPath: 'assets/vehicles/midnight-king.png',
    rarity: 'legendary',
    unlockedByDefault: false,
    unlock: 'distance_driver',
    accent: '#ff006e',
  },
];

export const DEFAULT_VEHICLE_ID = 'nairobi-legend';

/** Maps legacy skin ids from earlier builds. */
export const LEGACY_SKIN_MAP: Record<string, string> = {
  'nairobi-neon': 'nairobi-legend',
  'coast-breeze': 'coastal-cruiser',
  'highland-green': 'shujaa',
  'sunset-express': 'mtaa-express',
  'city-lightning': 'neon-beast',
};

export function getVehicle(id: string | null | undefined): MatatuDefinition {
  const mapped = id ? LEGACY_SKIN_MAP[id] ?? id : DEFAULT_VEHICLE_ID;
  return VEHICLES.find((v) => v.id === mapped) ?? VEHICLES[0]!;
}

export function vehicleSpriteKey(id: string): string {
  return getVehicle(id).spriteKey;
}

/** @deprecated Use VEHICLES — kept for older imports during transition. */
export const SKINS = VEHICLES.map((v) => ({
  id: v.id,
  name: v.name,
  unlock: v.unlock,
  colors: [0x111111, 0xffd166, 0xff6b1a] as [number, number, number],
  pattern: 'stripes' as const,
}));

export { ACHIEVEMENTS } from './achievements';
